// Global top app bar: menu toggles (hamburger on mobile, collapse on desktop),
// page title derived from the route, dark-mode toggle, and a user avatar menu
// with logout.

import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { useColorMode } from "theme/ColorModeContext";

// Route -> human title. Falls back to a derived label for unmapped paths.
const TITLES = {
  "/": "Home",
  "/dashboard": "Dashboard",
  "/visit-stats": "Visit Stats",
  "/task-stats": "Task Stats",
  "/update-location": "Dealer Location",
  "/holiday": "Holiday",
  "/attendance-data": "Attendance",
  "/dealer-churn": "Dealer Churn",
  "/dealer-help-desk": "Ticketing",
  "/actyv-ai": "Actyv AI",
  "/vists-download": "Reports",
  "/dealer-directory": "Dealer Directory",
  "/switch-beat": "Switch Beat Requests",
  "/sync": "Sync",
  "/onboard": "Onboard",
  "/market-research": "Market Research",
  "/wear-nature": "Wear Nature",
  "/survey": "Survey",
  "/survey-reconciliation": "Survey Reconciliation",
  "/compliance-inspect": "Compliance Inspector",
  "/upload-documents": "Upload Docs",
  "/se-depot": "SE Depot & User",
  "/se-reports": "SE Reports",
  "/admin-otr": "Admin OTR",
  "/documentation": "Documentation",
  "/app-maintenance": "App Maintenance",
  "/force-update": "Force Update",
};

const titleFor = (pathname) => {
  if (TITLES[pathname]) return TITLES[pathname];
  const match = Object.keys(TITLES).find(
    (p) => p !== "/" && pathname.startsWith(p)
  );
  if (match) return TITLES[match];
  const seg = pathname.split("/").filter(Boolean).pop() || "Home";
  return seg.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const Topbar = ({ appBarSx, onToggleMobile, onToggleCollapse }) => {
  const history = useHistory();
  const location = useLocation();
  const { mode, toggleColorMode } = useColorMode();
  const { user } = useSelector((state) => state.user);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const logout = () => {
    localStorage.setItem("token", "");
    history.push("/");
    window.location.reload();
  };

  const initials = (user?.name || user?.SAP_ID || "U")
    .toString()
    .trim()
    .slice(0, 2)
    .toUpperCase();

  return (
    <AppBar position="fixed" sx={appBarSx}>
      <Toolbar sx={{ gap: 1 }}>
        {/* Hamburger (mobile) + collapse (desktop) */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="toggle navigation"
          onClick={onToggleMobile}
          sx={{ display: { xs: "inline-flex", md: "none" } }}
        >
          <MenuIcon />
        </IconButton>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="collapse navigation"
          onClick={onToggleCollapse}
          sx={{ display: { xs: "none", md: "inline-flex" } }}
        >
          <MenuOpenIcon />
        </IconButton>

        <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
          {titleFor(location.pathname)}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {user?.SAP_ID && (
          <Chip
            size="small"
            color="primary"
            label={user.SAP_ID}
            sx={{ display: { xs: "none", sm: "inline-flex" }, fontWeight: 700 }}
          />
        )}

        <Tooltip title={mode === "dark" ? "Switch to light" : "Switch to dark"}>
          <IconButton color="inherit" onClick={toggleColorMode} aria-label="toggle theme">
            {mode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Account">
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ ml: 0.5 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", color: "primary.contrastText", fontSize: 14, fontWeight: 700 }}>
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem disabled sx={{ opacity: "1 !important" }}>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.name || user?.SAP_ID || "User"}
              </Typography>
              {user?.role && (
                <Typography variant="caption" color="text.secondary">
                  {user.role}
                </Typography>
              )}
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={logout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
